import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { PaperPlaneTilt, ArrowLeft, Trash, ChatCircle, MagnifyingGlass, X } from "@phosphor-icons/react";

const POLL_MS = 4000;

function fmtTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function Messages() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const nav = useNavigate();
    const { otherId: otherIdParam } = useParams();
    const [conversations, setConversations] = useState([]);
    const [selectedId, setSelectedId] = useState(otherIdParam || null);
    const [thread, setThread] = useState(null);  // { other_user, messages }
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [loadingThread, setLoadingThread] = useState(false);
    const [composing, setComposing] = useState(false);
    const [searchQ, setSearchQ] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const scrollRef = useRef(null);

    // --- Load conversations list (polled) ---
    const loadConversations = useCallback(async () => {
        try {
            const { data } = await api.get("/messages/conversations");
            setConversations(Array.isArray(data) ? data : []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        if (!user) return;
        loadConversations();
        const t = setInterval(loadConversations, POLL_MS * 2);
        return () => clearInterval(t);
    }, [user, loadConversations]);

    // --- Load a single thread ---
    const loadThread = useCallback(async (otherId) => {
        if (!otherId) { setThread(null); return; }
        setLoadingThread(true);
        try {
            const { data } = await api.get(`/messages/conversations/${otherId}`);
            setThread(data);
            // Refresh the conversations list (unread count will have dropped)
            loadConversations();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail) || t("common.error"));
            setThread(null);
        } finally {
            setLoadingThread(false);
        }
    }, [loadConversations, t]);

    useEffect(() => {
        if (selectedId) loadThread(selectedId);
    }, [selectedId, loadThread]);

    // Poll active thread
    useEffect(() => {
        if (!selectedId) return;
        const t = setInterval(() => { loadThread(selectedId); }, POLL_MS);
        return () => clearInterval(t);
    }, [selectedId, loadThread]);

    // Scroll to bottom on new messages
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [thread?.messages?.length]);

    // --- Send message ---
    const send = async () => {
        const text = input.trim();
        if (!text || !selectedId || sending) return;
        setSending(true);
        // Optimistic append
        const tempId = "local-" + Date.now();
        setThread((prev) => prev ? {
            ...prev,
            messages: [...prev.messages, {
                id: tempId,
                from_user_id: user.id,
                to_user_id: selectedId,
                text,
                created_at: new Date().toISOString(),
                _pending: true,
            }],
        } : prev);
        setInput("");
        try {
            await api.post("/messages/send", { to_user_id: selectedId, text });
            loadThread(selectedId);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail) || t("common.error"));
            // Rollback optimistic message
            setThread((prev) => prev ? { ...prev, messages: prev.messages.filter((m) => m.id !== tempId) } : prev);
        } finally {
            setSending(false);
        }
    };

    // --- Delete full conversation ---
    const onDelete = async () => {
        if (!selectedId) return;
        if (!window.confirm(t("messages.delete_conversation_confirm"))) return;
        try {
            await api.delete(`/messages/conversations/${selectedId}`);
            toast.success(t("messages.message_deleted"));
            setThread(null);
            setSelectedId(null);
            nav("/messages");
            loadConversations();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    // --- User search for new conversation ---
    useEffect(() => {
        if (!composing || searchQ.trim().length < 2) { setSearchResults([]); return; }
        let cancelled = false;
        const id = setTimeout(async () => {
            try {
                const { data } = await api.get(`/users/search?q=${encodeURIComponent(searchQ.trim())}`);
                if (!cancelled) setSearchResults((data || []).filter((u) => u.id !== user?.id).slice(0, 8));
            } catch { /* silent */ }
        }, 250);
        return () => { cancelled = true; clearTimeout(id); };
    }, [searchQ, composing, user?.id]);

    const startWith = (other) => {
        setComposing(false);
        setSearchQ("");
        setSearchResults([]);
        setSelectedId(other.id);
        nav(`/messages/${other.id}`);
    };

    if (!user) {
        return (
            <div className="p-8 text-center">
                <Link to="/login" className="btn-primary inline-block px-6 py-2 rounded-sm">
                    {t("nav.login")}
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="messages-page" className="flex h-[calc(100dvh-3rem)] md:h-screen">
            {/* Sidebar: conversations list */}
            <aside className={`${selectedId ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 border-r border-wta-border bg-wta-surface shrink-0`}>
                <div className="px-4 py-3 border-b border-wta-border flex items-center justify-between">
                    <h1 className="font-heading text-xl uppercase tracking-wider">{t("messages.title")}</h1>
                    <button
                        data-testid="new-message-btn"
                        onClick={() => setComposing(true)}
                        className="btn-primary text-xs px-3 py-1.5 rounded-sm"
                    >
                        + {t("messages.new_message")}
                    </button>
                </div>

                {composing && (
                    <div className="p-3 border-b border-wta-border bg-wta-surface2">
                        <div className="flex items-center gap-2">
                            <MagnifyingGlass size={14} />
                            <input
                                autoFocus
                                type="text"
                                data-testid="new-message-search"
                                value={searchQ}
                                onChange={(e) => setSearchQ(e.target.value)}
                                placeholder={t("messages.search_users")}
                                className="flex-1 bg-transparent border-none focus:outline-none text-sm"
                            />
                            <button onClick={() => { setComposing(false); setSearchQ(""); setSearchResults([]); }} className="text-wta-muted hover:text-white">
                                <X size={14} />
                            </button>
                        </div>
                        {searchResults.length > 0 && (
                            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                                {searchResults.map((r) => (
                                    <button
                                        key={r.id}
                                        data-testid={`search-result-${r.name}`}
                                        onClick={() => startWith(r)}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-white/5 rounded-sm"
                                    >
                                        <Avatar user={r} />
                                        <span className="flex-1 text-left truncate">@{r.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto">
                    {conversations.length === 0 ? (
                        <div className="p-8 text-center text-wta-muted text-sm">
                            <ChatCircle size={36} className="mx-auto mb-2 opacity-50" />
                            <div>{t("messages.no_conversations")}</div>
                            <div className="mt-1 text-xs opacity-70">{t("messages.start_a_conversation")}</div>
                        </div>
                    ) : (
                        conversations.map((c) => {
                            const active = selectedId === c.other_user.id;
                            return (
                                <button
                                    key={c.conversation_key}
                                    data-testid={`conversation-${c.other_user.name}`}
                                    onClick={() => { setSelectedId(c.other_user.id); nav(`/messages/${c.other_user.id}`); }}
                                    className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-wta-border/50 transition ${
                                        active ? "bg-wta-primary/10" : "hover:bg-white/5"
                                    }`}
                                >
                                    <Avatar user={c.other_user} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="font-heading text-sm tracking-wide truncate">@{c.other_user.name}</div>
                                            <div className="text-[10px] text-wta-muted shrink-0">{fmtTime(c.last_message_at)}</div>
                                        </div>
                                        <div className="flex items-center justify-between gap-2 mt-0.5">
                                            <div className="text-xs text-white/60 truncate">
                                                {c.last_message_from === user.id && <span className="text-wta-muted">{t("messages.you")}: </span>}
                                                {c.last_message_text}
                                            </div>
                                            {c.unread_count > 0 && (
                                                <span data-testid={`unread-${c.other_user.name}`} className="bg-wta-primary text-white text-[10px] font-heading tracking-wider px-1.5 py-0.5 rounded-full shrink-0">
                                                    {c.unread_count}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </aside>

            {/* Thread */}
            <section className={`${selectedId ? "flex" : "hidden md:flex"} flex-1 flex-col bg-wta-bg min-w-0`}>
                {selectedId && thread ? (
                    <>
                        {/* Thread header */}
                        <header className="flex items-center gap-3 px-4 py-3 border-b border-wta-border bg-wta-surface">
                            <button
                                data-testid="thread-back"
                                onClick={() => { setSelectedId(null); setThread(null); nav("/messages"); }}
                                className="md:hidden text-wta-muted hover:text-white"
                            >
                                <ArrowLeft size={18} />
                            </button>
                            <Link to={`/profile/${thread.other_user.id}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80">
                                <Avatar user={thread.other_user} />
                                <div className="min-w-0">
                                    <div className="font-heading text-sm tracking-wide truncate">@{thread.other_user.name}</div>
                                    <div className="text-[10px] text-wta-muted truncate">{t("feed.view_profile")}</div>
                                </div>
                            </Link>
                            <button
                                data-testid="delete-conversation-btn"
                                onClick={onDelete}
                                className="text-wta-muted hover:text-red-400 p-2"
                                title={t("messages.delete_conversation")}
                            >
                                <Trash size={16} />
                            </button>
                        </header>

                        {/* Messages */}
                        <div ref={scrollRef} data-testid="thread-messages" className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
                            {thread.messages.length === 0 ? (
                                <div className="text-center text-wta-muted text-xs pt-8">{t("messages.start_a_conversation")}</div>
                            ) : (
                                thread.messages.map((m) => {
                                    const mine = m.from_user_id === user.id;
                                    return (
                                        <div
                                            key={m.id}
                                            data-testid={`msg-${m.id}`}
                                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                        >
                                            <div
                                                className={`max-w-[75%] px-3 py-2 rounded-md text-sm whitespace-pre-wrap break-words ${
                                                    mine
                                                        ? "bg-wta-primary text-white rounded-br-none"
                                                        : "bg-wta-surface2 border border-wta-border rounded-bl-none"
                                                } ${m._pending ? "opacity-50" : ""}`}
                                            >
                                                {m.text}
                                                <div className={`text-[9px] mt-1 ${mine ? "text-white/70" : "text-wta-muted"} text-right`}>
                                                    {fmtTime(m.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Composer */}
                        <div className="border-t border-wta-border bg-wta-surface p-3 flex items-end gap-2">
                            <textarea
                                data-testid="message-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                                placeholder={t("messages.type_a_message")}
                                rows={1}
                                maxLength={2000}
                                className="flex-1 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm resize-none max-h-32 focus:border-wta-primary focus:outline-none"
                            />
                            <button
                                data-testid="message-send"
                                onClick={send}
                                disabled={!input.trim() || sending}
                                className="btn-primary rounded-sm w-10 h-10 flex items-center justify-center disabled:opacity-40"
                                aria-label={t("messages.send")}
                            >
                                <PaperPlaneTilt size={16} weight="fill" />
                            </button>
                        </div>
                    </>
                ) : loadingThread ? (
                    <div className="flex-1 flex items-center justify-center text-wta-muted text-sm">{t("common.loading")}</div>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-wta-muted text-sm">
                        <div className="text-center">
                            <ChatCircle size={48} className="mx-auto mb-3 opacity-40" />
                            <div>{t("messages.start_a_conversation")}</div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}

function Avatar({ user }) {
    return (
        <div className="w-10 h-10 rounded-full bg-wta-surface2 border border-wta-border flex items-center justify-center font-heading text-sm overflow-hidden shrink-0">
            {user?.profile_pic ? (
                <img src={user.profile_pic} alt="" className="w-full h-full object-cover" />
            ) : (
                <span>{user?.name?.[0]?.toUpperCase() || "?"}</span>
            )}
        </div>
    );
}
